import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Users,
  Building2,
  Phone,
  MapPin,
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  Link,
  RefreshCw,
  BadgeCheck,
  ShieldCheck,
  Key,
  Eye
} from "lucide-react";

import { useRef } from "react";

const buyerTypes = [
  { value: "EST", label: "EST" },
  { value: "INV", label: "INV" },
];

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    cust_code: "",
    name: "",
    company: "",
    buyer_type: "",
    billing_name: "",
    billing_address: "",
    dispatch_name: "",
    dispatch_city: "",
    dispatch_code: "",
    distance_km: "",
    city: "",
    pincode: "",
    state: "",
    gstin: "",
    phone: "",
    phone2: "",
    email: "",
    transporter_name: "",
    transporter_address: "",
    transporter_gstin: "",
    transporter_contact: "",
    broker_name: "",
    customer_type: "",
    default_payment_terms: "",
    opening_balance: "",
    opening_balance_type: "debit"
  });
  const [comboCfg, setComboCfg] = useState({ enabled: false, target_avg: "", shuffle_limit: 2 });
  
  // Filter states
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  const [showBotCreatedOnly, setShowBotCreatedOnly] = useState(false);
  
  // Selected customer for detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  
  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Google Sheet sync state
  const [sheetSyncDialogOpen, setSheetSyncDialogOpen] = useState(false);
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetConfig, setSheetConfig] = useState({
    customer_sheet_id: "",
    customer_sheet_tab: "Customers"
  });
  const [sheetSyncStatus, setSheetSyncStatus] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchSheetSyncStatus();
  }, []);

  const fetchSheetSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/customers/sheet-sync-status`);
      setSheetSyncStatus(response.data);
      if (response.data.customer_sheet_id) {
        setSheetConfig({
          customer_sheet_id: response.data.customer_sheet_id || "",
          customer_sheet_tab: response.data.customer_sheet_tab || "Customers"
        });
      }
    } catch (error) {
    }
  };

  const handleSaveSheetConfig = async () => {
    try {
      await axios.put(`${API}/settings`, {
        customer_sheet_id: sheetConfig.customer_sheet_id,
        customer_sheet_tab: sheetConfig.customer_sheet_tab
      });
      toast.success("Sheet configuration saved");
      fetchSheetSyncStatus();
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const handleSyncFromSheet = async () => {
    setSheetSyncing(true);
    try {
      const response = await axios.post(`${API}/customers/sync-from-sheet`);
      if (response.data.success) {
        toast.success(`Synced! Created: ${response.data.created}, Updated: ${response.data.updated}`);
        fetchCustomers();
        fetchSheetSyncStatus();
        setSheetSyncDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync from Google Sheet");
    } finally {
      setSheetSyncing(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data with proper types - convert empty strings to null for numeric fields
      const submitData = {
        ...formData,
        distance_km: formData.distance_km !== '' && formData.distance_km !== null && formData.distance_km !== undefined ? parseFloat(formData.distance_km) : null,
        opening_balance: formData.opening_balance !== '' && formData.opening_balance !== null && formData.opening_balance !== undefined ? parseFloat(formData.opening_balance) : 0
      };
      
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, submitData);
        toast.success("Customer updated successfully");
      } else {
        await axios.post(`${API}/customers`, submitData);
        toast.success("Customer added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save customer");
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      cust_code: customer.cust_code || "",
      name: customer.name,
      company: customer.company || "",
      buyer_type: customer.buyer_type || "",
      billing_name: customer.billing_name || "",
      billing_address: customer.billing_address || "",
      dispatch_name: customer.dispatch_name || "",
      dispatch_city: customer.dispatch_city || "",
      dispatch_code: customer.dispatch_code || "",
      distance_km: customer.distance_km || "",
      city: customer.city || "",
      pincode: customer.pincode || "",
      state: customer.state || "",
      gstin: customer.gstin || "",
      phone: customer.phone,
      phone2: customer.phone2 || "",
      email: customer.email || "",
      transporter_name: customer.transporter_name || "",
      transporter_address: customer.transporter_address || "",
      transporter_gstin: customer.transporter_gstin || "",
      transporter_contact: customer.transporter_contact || "",
      broker_name: customer.broker_name || customer.group_name || "",
      customer_type: customer.customer_type || "",
      default_payment_terms: customer.default_payment_terms || "",
      opening_balance: customer.opening_balance || "",
      opening_balance_type: customer.opening_balance_type || "debit"
    });
    setComboCfg({
      enabled: !!customer.combo_enabled,
      target_avg: customer.combo_target_avg || "",
      shuffle_limit: customer.combo_shuffle_limit || 2,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    // Open confirmation dialog instead of window.confirm
    setCustomerToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Complete onboarding for bot-created customer
  const handleCompleteOnboarding = async (customerId) => {
    try {
      const response = await axios.post(`${API}/customers/${customerId}/complete-onboarding`);
      toast.success(response.data.message);
      setSelectedCustomer(prev => prev ? {...prev, verified: true, onboarding_completed: true} : null);
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to complete onboarding");
    }
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    try {
      await axios.delete(`${API}/customers/${customerToDelete}`);
      toast.success("Customer deleted");
      setSelectedCustomer(null);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (error) {
      // error handled by toast
      toast.error("Failed to delete customer");
    }
  };

  const handleVerify = async (customerId) => {
    try {
      const response = await axios.post(`${API}/customers/${customerId}/verify`);
      toast.success(response.data.message);
      // Update selected customer and refresh list
      setSelectedCustomer(prev => prev ? {...prev, verified: response.data.verified} : null);
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to update verification status");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/customers/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(response.data);
      toast.success(`Uploaded ${response.data.created} customers, Updated ${response.data.updated}`);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload Excel");
      setUploadResult({ error: error.response?.data?.detail || "Upload failed" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/customers/download-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const exportAllCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/export-all`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `customers_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Customers exported to Excel");
    } catch (error) {
      toast.error("Failed to export customers");
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      cust_code: "",
      name: "",
      company: "",
      buyer_type: "",
      billing_name: "",
      billing_address: "",
      dispatch_name: "",
      dispatch_city: "",
      dispatch_code: "",
      distance_km: "",
      city: "",
      pincode: "",
      state: "",
      gstin: "",
      phone: "",
      phone2: "",
      email: "",
      transporter_name: "",
      transporter_address: "",
      transporter_gstin: "",
      transporter_contact: "",
      broker_name: "",
      customer_type: "",
      default_payment_terms: "",
      opening_balance: "",
      opening_balance_type: "debit"
    });
    setComboCfg({ enabled: false, target_avg: "", shuffle_limit: 2 });
  };

  const filteredCustomers = customers
    .filter(customer => {
      // Search filter
      const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm);
      
      // Unverified filter
      if (showUnverifiedOnly && customer.verified) return false;
      
      // Bot created filter
      if (showBotCreatedOnly && customer.source !== 'bot') return false;
      
      return matchesSearch;
    })
    .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
  
  // Count unverified and bot-created customers
  const unverifiedCount = customers.filter(c => !c.verified).length;
  const botCreatedCount = customers.filter(c => c.source === 'bot').length;

  const getBuyerTypeBadge = (type) => {
    const colors = {
      wholesaler: "bg-blue-100 text-blue-800 border-blue-200",
      trader: "bg-purple-100 text-purple-800 border-purple-200",
      exporter: "bg-green-100 text-green-800 border-green-200",
      processor: "bg-orange-100 text-orange-800 border-orange-200"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="p-8" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Customers
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your B2B customers
          </p>
        </div>
        <div className="flex gap-3">
          {/* Google Sheet Sync Dialog */}
          <Dialog open={sheetSyncDialogOpen} onOpenChange={setSheetSyncDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="sheet-sync-btn">
                <Link className="w-4 h-4" />
                Google Sheet
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Sync Customers from Google Sheet
                </DialogTitle>
                <DialogDescription>
                  Connect your Google Sheet to import/sync customers
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Sync Status */}
                <div className={`p-3 rounded-lg border ${sheetSyncStatus?.last_customer_sync ? 'bg-green-50 border-green-200' : 'bg-secondary/50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {sheetSyncStatus?.last_customer_sync ? (
                        <><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Last synced: {new Date(sheetSyncStatus.last_customer_sync).toLocaleString()}</span></>
                      ) : (
                        <><AlertCircle className="w-4 h-4 text-amber-600" /><span className="text-sm">Not yet synced</span></>
                      )}
                    </div>
                  </div>
                </div>

                {/* Configuration */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="customer_sheet_id">Google Sheet ID *</Label>
                    <Input
                      id="customer_sheet_id"
                      value={sheetConfig.customer_sheet_id}
                      onChange={(e) => setSheetConfig({...sheetConfig, customer_sheet_id: e.target.value})}
                      placeholder="1vdNOxknPCwYFJrmX_6-SzcxGH8XYXpfEPYjpj..."
                    />
                    <p className="text-xs text-muted-foreground">
                      From URL: docs.google.com/spreadsheets/d/<strong>SHEET_ID</strong>/edit
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_sheet_tab">Tab Name</Label>
                    <Input
                      id="customer_sheet_tab"
                      value={sheetConfig.customer_sheet_tab}
                      onChange={(e) => setSheetConfig({...sheetConfig, customer_sheet_tab: e.target.value})}
                      placeholder="Customers"
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div className="p-3 bg-secondary/50 rounded-lg text-sm space-y-1">
                  <p className="font-medium">Requirements:</p>
                  <div className="flex items-center gap-2">
                    {sheetSyncStatus?.has_google_credentials ? (
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-amber-600" />
                    )}
                    <span>Google Service Account (Settings → Sheets)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(sheetConfig.customer_sheet_id || sheetSyncStatus?.using_main_sheet) ? (
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-amber-600" />
                    )}
                    <span>
                      {sheetSyncStatus?.using_main_sheet 
                        ? "Using main Google Sheet (from Settings)" 
                        : "Sheet ID configured"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Required columns: PARTY NAME, CONTACT No.
                  </p>
                  {sheetSyncStatus?.using_main_sheet && (
                    <p className="text-xs text-blue-600 mt-1">
                      ℹ️ Leave Sheet ID empty to use the main sheet from Settings → Sheets
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSaveSheetConfig} className="flex-1">
                    Save Config
                  </Button>
                  <Button 
                    onClick={handleSyncFromSheet} 
                    disabled={sheetSyncing || (!sheetConfig.customer_sheet_id && !sheetSyncStatus?.using_main_sheet) || !sheetSyncStatus?.has_google_credentials}
                    className="flex-1 gap-2"
                  >
                    {sheetSyncing ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Syncing...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4" /> Sync Now</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Export All Customers */}
          <Button variant="outline" onClick={exportAllCustomers} className="gap-2" data-testid="export-customers-btn">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>

          {/* Upload Customers Dialog */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="upload-customers-btn">
                <Upload className="w-4 h-4" />
                Upload Customers
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Upload Customers
                </DialogTitle>
                <DialogDescription>
                  Upload an Excel file to bulk import customers
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload Excel file (.xlsx) with columns:<br/>
                    Name, Company, Customer Type, City, State, Phone
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="customer-excel-upload"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-2"
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Select Excel File</>
                    )}
                  </Button>
                </div>
                
                <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2">
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
                
                {uploadResult && (
                  <div className={`p-4 rounded-lg ${uploadResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    {uploadResult.error ? (
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        <span>{uploadResult.error}</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">Upload Successful!</span>
                        </div>
                        <p className="text-sm text-green-600">
                          Created: {uploadResult.created} customers<br/>
                          Updated: {uploadResult.updated} existing customers
                        </p>
                        {uploadResult.errors?.length > 0 && (
                          <div className="text-sm text-red-600 mt-2">
                            <p className="font-medium">Errors:</p>
                            {uploadResult.errors.slice(0, 5).map((err, i) => (
                              <p key={i}>{err}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Customer Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-customer-btn">
              <Plus className="w-4 h-4" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                {editingCustomer ? "Edit Customer" : "Add New Customer"}
              </DialogTitle>
              <DialogDescription>
                {editingCustomer ? "Update customer details below" : "Fill in the complete customer details"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Basic Info Section */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-800 mb-3">Basic Information</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="cust_code" className="text-xs">Customer Code</Label>
                    <Input
                      id="cust_code"
                      value={formData.cust_code}
                      onChange={(e) => setFormData({ ...formData, cust_code: e.target.value })}
                      placeholder="C001"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="name" className="text-xs">Party Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-8 text-sm"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="company" className="text-xs">Company/Firm</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label htmlFor="phone" className="text-xs">Contact No. *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      placeholder="+91..."
                      className="h-8 text-sm"
                      data-testid="customer-phone-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="phone2" className="text-xs">Contact No. 2</Label>
                    <Input
                      id="phone2"
                      value={formData.phone2}
                      onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                      placeholder="+91..."
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="email" className="text-xs">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Details Section */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-sm font-semibold text-green-800 mb-3">Billing Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="billing_name" className="text-xs">Billing Name</Label>
                    <Input
                      id="billing_name"
                      value={formData.billing_name}
                      onChange={(e) => setFormData({ ...formData, billing_name: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="gstin" className="text-xs">GSTIN</Label>
                    <Input
                      id="gstin"
                      value={formData.gstin}
                      onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                      placeholder="27XXXXX1234X1ZX"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1 mt-3">
                  <Label htmlFor="billing_address" className="text-xs">Billing Address</Label>
                  <Input
                    id="billing_address"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              {/* Dispatch Details Section */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-semibold text-amber-800 mb-3">Dispatch Details</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="dispatch_name" className="text-xs">Dispatch Name</Label>
                    <Input
                      id="dispatch_name"
                      value={formData.dispatch_name}
                      onChange={(e) => setFormData({ ...formData, dispatch_name: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dispatch_city" className="text-xs">Dispatch City</Label>
                    <Input
                      id="dispatch_city"
                      value={formData.dispatch_city}
                      onChange={(e) => setFormData({ ...formData, dispatch_city: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dispatch_code" className="text-xs">Dispatch Code</Label>
                    <Input
                      id="dispatch_code"
                      value={formData.dispatch_code}
                      onChange={(e) => setFormData({ ...formData, dispatch_code: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="distance_km" className="text-xs">Distance (KMs)</Label>
                    <Input
                      id="distance_km"
                      type="number"
                      value={formData.distance_km}
                      onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                      className="h-8 text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label htmlFor="city" className="text-xs">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="state" className="text-xs">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="pincode" className="text-xs">Pincode</Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Transporter Details Section */}
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="text-sm font-semibold text-purple-800 mb-3">Transporter Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="transporter_name" className="text-xs">Transporter Name</Label>
                    <Input
                      id="transporter_name"
                      value={formData.transporter_name}
                      onChange={(e) => setFormData({ ...formData, transporter_name: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transporter_contact" className="text-xs">Transporter Contact</Label>
                    <Input
                      id="transporter_contact"
                      value={formData.transporter_contact}
                      onChange={(e) => setFormData({ ...formData, transporter_contact: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label htmlFor="transporter_address" className="text-xs">Branch Address</Label>
                    <Input
                      id="transporter_address"
                      value={formData.transporter_address}
                      onChange={(e) => setFormData({ ...formData, transporter_address: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transporter_gstin" className="text-xs">Transporter GSTIN</Label>
                    <Input
                      id="transporter_gstin"
                      value={formData.transporter_gstin}
                      onChange={(e) => setFormData({ ...formData, transporter_gstin: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Classification Section */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Classification</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="buyer_type" className="text-xs">Customer Type</Label>
                    <Select
                      value={formData.buyer_type}
                      onValueChange={(value) => setFormData({ ...formData, buyer_type: value })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {buyerTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="customer_type" className="text-xs">Customer Type</Label>
                    <Select
                      value={formData.customer_type || ''}
                      onValueChange={(value) => setFormData({ ...formData, customer_type: value })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dealer">Dealer</SelectItem>
                        <SelectItem value="Distributor">Distributor</SelectItem>
                        <SelectItem value="Retailer">Retailer</SelectItem>
                        <SelectItem value="Wholesaler">Wholesaler</SelectItem>
                        <SelectItem value="Exporter">Exporter</SelectItem>
                        <SelectItem value="Processor">Processor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="broker_name" className="text-xs">Broker Name</Label>
                    <Input
                      id="broker_name"
                      value={formData.broker_name}
                      onChange={(e) => setFormData({ ...formData, broker_name: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="default_payment_terms" className="text-xs">Payment Terms</Label>
                    <Select
                      value={formData.default_payment_terms || ''}
                      onValueChange={(value) => setFormData({ ...formData, default_payment_terms: value })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ADV">ADV</SelectItem>
                        <SelectItem value="REG">REG</SelectItem>
                        <SelectItem value="DDAP">DDAP</SelectItem>
                        <SelectItem value="RDAP">RDAP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 mt-3">
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Opening Balance</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={formData.opening_balance}
                        onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                        className="h-8 text-sm flex-1"
                        data-testid="opening-balance-input"
                      />
                      <Select
                        value={formData.opening_balance_type}
                        onValueChange={(value) => setFormData({ ...formData, opening_balance_type: value })}
                      >
                        <SelectTrigger className="h-8 text-sm w-24" data-testid="opening-balance-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="debit">Debit</SelectItem>
                          <SelectItem value="credit">Credit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-customer-btn">
                  {editingCustomer ? "Update" : "Add"} Customer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-customers-input"
          />
        </div>
        
        {/* Filter Buttons */}
        {unverifiedCount > 0 && (
          <Button 
            variant={showUnverifiedOnly ? "default" : "outline"} 
            size="sm" 
            className={`gap-1.5 ${showUnverifiedOnly ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
            onClick={() => { setShowUnverifiedOnly(!showUnverifiedOnly); setShowBotCreatedOnly(false); }}
          >
            <AlertCircle className="w-3.5 h-3.5" />
            Unverified ({unverifiedCount})
          </Button>
        )}
        
        {botCreatedCount > 0 && (
          <Button 
            variant={showBotCreatedOnly ? "default" : "outline"} 
            size="sm" 
            className={`gap-1.5 ${showBotCreatedOnly ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            onClick={() => { setShowBotCreatedOnly(!showBotCreatedOnly); setShowUnverifiedOnly(false); }}
          >
            <Users className="w-3.5 h-3.5" />
            Bot Created ({botCreatedCount})
          </Button>
        )}
      </div>

      {/* Customers Table */}
      <Card className="border-border/60">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading customers...</p>
            </div>
          ) : filteredCustomers.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Customer List */}
              <div className="lg:col-span-1 border-r border-border/60">
                <div className="max-h-[600px] overflow-y-auto">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className={`flex items-center gap-3 p-3 cursor-pointer border-b border-border/40 transition-colors ${
                        selectedCustomer?.id === customer.id 
                          ? 'bg-primary/10 border-l-4 border-l-primary' 
                          : 'hover:bg-secondary/50'
                      }`}
                      data-testid={`customer-item-${customer.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium truncate">{customer.name}</p>
                          {customer.verified && (
                            <BadgeCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {customer.dispatch_city || customer.city || 'No location'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Detail View */}
              <div className="lg:col-span-2 p-4 max-h-[600px] overflow-y-auto">
                {selectedCustomer ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                          {selectedCustomer.name}
                        </h2>
                        {selectedCustomer.verified && (
                          <BadgeCheck className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant={selectedCustomer.verified ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleVerify(selectedCustomer.id)}
                          className={`gap-1 ${selectedCustomer.verified ? 'text-green-600 border-green-600' : ''}`}
                          data-testid="verify-customer-btn"
                        >
                          <ShieldCheck className="w-4 h-4" />
                          {selectedCustomer.verified ? 'Verified' : 'Verify'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(selectedCustomer)}
                          className="gap-1"
                          data-testid="edit-customer-btn"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(selectedCustomer.id);
                          }}
                          className="gap-1"
                          data-testid="delete-customer-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                        {/* Complete Onboarding for bot-created unverified customers */}
                        {selectedCustomer.source === 'bot' && !selectedCustomer.onboarding_completed && (
                          <Button
                            size="sm"
                            className="gap-1 bg-green-600 hover:bg-green-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompleteOnboarding(selectedCustomer.id);
                            }}
                            data-testid="complete-onboarding-btn"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Complete Onboarding
                          </Button>
                        )}
                        {/* Create Login for Customer */}
                        {!selectedCustomer.has_login && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const resp = await axios.post(`${API}/customers/${selectedCustomer.id}/create-login`);
                                toast.success(`Login created: ${resp.data.username} / ${resp.data.password}`);
                                fetchCustomers();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Failed to create login");
                              }
                            }}
                            data-testid="create-customer-login-btn"
                          >
                            <Key className="w-4 h-4" />
                            Create Login
                          </Button>
                        )}
                        {selectedCustomer.has_login && (
                          <Badge className="bg-indigo-100 text-indigo-800 text-[10px] gap-1">
                            <Key className="w-3 h-3" /> Login Active
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => navigate(`/customer-view/${encodeURIComponent(selectedCustomer.name)}`)}
                          data-testid="view-customer-dashboard-btn"
                        >
                          <Eye className="w-4 h-4" />
                          View Dashboard
                        </Button>

                      </div>
                    </div>

                    {/* Source indicator */}
                    {selectedCustomer.source === 'bot' && (
                      <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800">Bot Created Customer</span>
                        {selectedCustomer.onboarding_completed ? (
                          <Badge className="ml-auto bg-green-100 text-green-800">Onboarded</Badge>
                        ) : (
                          <Badge className="ml-auto bg-amber-100 text-amber-800">Pending Onboarding</Badge>
                        )}
                      </div>
                    )}

                    {/* Excel-like detail grid */}
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium w-1/3 border-r">CUST CODE</td>
                            <td className="p-3">{selectedCustomer.cust_code || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">PARTY NAME</td>
                            <td className="p-3">{selectedCustomer.name || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">DISPATCH NAME</td>
                            <td className="p-3">{selectedCustomer.dispatch_name || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">BILLING NAME</td>
                            <td className="p-3">{selectedCustomer.billing_name || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">BILLING ADDRESS</td>
                            <td className="p-3">{selectedCustomer.billing_address || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">DISPATCH CITY</td>
                            <td className="p-3">{selectedCustomer.dispatch_city || selectedCustomer.city || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">PINCODE</td>
                            <td className="p-3">{selectedCustomer.pincode || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">STATE</td>
                            <td className="p-3">{selectedCustomer.state || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">GSTIN</td>
                            <td className="p-3">{selectedCustomer.gstin || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">DISPATCH CODE</td>
                            <td className="p-3">{selectedCustomer.dispatch_code || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">DISTANCE (KMs)</td>
                            <td className="p-3">{selectedCustomer.distance_km ? `${selectedCustomer.distance_km} km` : "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">CONTACT No.</td>
                            <td className="p-3">{selectedCustomer.phone || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">CONTACT No. 2</td>
                            <td className="p-3">{selectedCustomer.phone2 || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">EMAIL ID</td>
                            <td className="p-3">{selectedCustomer.email || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">TRANSPORTER NAME</td>
                            <td className="p-3">{selectedCustomer.transporter_name || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">BRANCH ADDRESS</td>
                            <td className="p-3">{selectedCustomer.transporter_address || "-"}</td>
                          </tr>
                          <tr className="border-b bg-secondary/30">
                            <td className="p-3 font-medium border-r">TRANSPORTER GSTIN</td>
                            <td className="p-3">{selectedCustomer.transporter_gstin || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">TRANSPORTER CONTACT No.</td>
                            <td className="p-3">{selectedCustomer.transporter_contact || "-"}</td>
                          </tr>
                          <tr className="bg-secondary/30">
                            <td className="p-3 font-medium border-r">BROKER NAME</td>
                            <td className="p-3">{selectedCustomer.broker_name || selectedCustomer.group_name || "-"}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 font-medium border-r bg-secondary/30">TYPE</td>
                            <td className="p-3">{selectedCustomer.customer_type || "-"}</td>
                          </tr>
                          <tr className="bg-secondary/30">
                            <td className="p-3 font-medium border-r">PAYMENT TERMS</td>
                            <td className="p-3">{selectedCustomer.default_payment_terms || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[400px] text-center">
                    <Users className="w-16 h-16 mb-4 text-muted-foreground/30" />
                    <p className="text-lg font-medium text-muted-foreground">Select a customer</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">
                      Click on a customer from the list to view all details
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state py-16">
              <Users className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">No customers found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm ? "Try a different search term" : "Add your first B2B customer to get started"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this customer? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCustomerToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
